/**
 * @file Context: TitleMusicContext
 * @description Context for managing title music that persists across screens
 */

import type React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTitleMusicAutoplay } from '../hooks/useTitleMusicAutoplay';

interface TitleMusicContextType {
  isPlaying: boolean;
  startTitleMusic: () => void;
  stopTitleMusic: () => void;
}
const TitleMusicContext = createContext<TitleMusicContextType>({
  isPlaying: false,
  startTitleMusic: () => {},
  stopTitleMusic: () => {},
});

export const useTitleMusic = () => useContext(TitleMusicContext);

export const TitleMusicProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const { play, stop, sound } = useTitleMusicAutoplay();

  const firstLoadRef = useRef(true);
  const wantsToPlayRef = useRef(false);

  const startTitleMusic = useCallback(() => {
    // Prevent double playing (Fixes Bad Sound)
    if (sound?.playing()) {
      setIsPlaying(true);
      wantsToPlayRef.current = true;
      return;
    }

    wantsToPlayRef.current = true;

    // Force reset the state in localStorage and sessionStorage
    localStorage.setItem('titleMusic_isPlaying', 'true');

    try {
      sessionStorage.setItem('titleMusicStarted', 'true');
    } catch (e) {
      console.error('Failed to set session storage:', e);
    }

    if (sound) {
      if (sound.state() === 'unloaded') {
        sound.load();
      }

      play();
      setIsPlaying(true);
    }
    // If sound is not ready yet, the auto-start effect below will
    // pick it up once Howler finishes loading the audio file.
  }, [sound, play]);

  const stopTitleMusic = useCallback(() => {
    wantsToPlayRef.current = false;
    if (sound) {
      sound.stop();
    }
    stop();
    setIsPlaying(false);
    localStorage.setItem('titleMusic_isPlaying', 'false');
  }, [sound, stop]);

  // Auto-start music when sound becomes available after async Howler loading.
  // Replaces the previous setTimeout fallback which suffered from stale closures.
  useEffect(() => {
    if (sound && wantsToPlayRef.current && !sound.playing()) {
      play();
      setIsPlaying(true);
    }
  }, [sound, play]);

  useEffect(() => {
    if (firstLoadRef.current) {
      firstLoadRef.current = false;
      const shouldPlay =
        localStorage.getItem('titleMusic_isPlaying') !== 'false';

      if (shouldPlay) {
        startTitleMusic();
      }
    }
  }, [startTitleMusic]);

  return (
    <TitleMusicContext.Provider
      value={{
        isPlaying,
        startTitleMusic,
        stopTitleMusic,
      }}
    >
      {children}
    </TitleMusicContext.Provider>
  );
};
